import React, { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAuthStore } from "../stores/auth-store";
import { Dialog } from "../components/UI/Dialog";
import { LoginModal } from "../components/UI/LoginModal";
import { Pod24CommonLayout } from "../layouts/Pod24CommonLayout";

export const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { isAuthenticated, user, logout, isLoading } = useAuthStore();

  const handleLogin = () => {
    setShowLoginModal(true);
  };

  const handleDashboard = () => {
    navigate("/app/dashboard");
  };

  const handleSignUp = () => {
    navigate("/signup");
  };

  const handleAdminPage = () => {
    navigate("/app/dashboard");
  };

  const handleServiceRequest = () => {
    // TODO: 서비스 신청 페이지로 이동
    navigate("/service-request");
  };

  const handleLogout = async () => {
    try {
      await logout();
      setShowLogoutDialog(false);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    // Add smooth scrolling to the entire document
    document.documentElement.style.scrollBehavior = "smooth";

    return () => {
      document.documentElement.style.scrollBehavior = "auto";
    };
  }, []);

  return (
    <>
      <Pod24CommonLayout showAuth={true}>
        <div className="min-h-screen bg-white">
          {/* Hero Section - Responsive with Figma styling */}
          <section
            id="home"
            className="relative bg-cover bg-center overflow-hidden h-[500px] md:h-[600px]"
          >
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('/assets/images/city-863692.png')`,
              }}
            />
            <div className="absolute inset-0 bg-black bg-opacity-60" />

            <div className="relative z-10 flex flex-col h-full text-center text-white px-4">
              <div className="flex-1 flex flex-col items-center justify-center">
                <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold font-['Gmarket_Sans_TTF'] mb-4 md:mb-6 max-w-4xl lg:text-[60px] lg:w-[878px] lg:h-[222px] lg:flex lg:items-center lg:justify-center leading-[1.4] sm:leading-[1.3] md:leading-[1.35] lg:leading-[1.35] break-keep">
                  중소기업을 위한
                  <br />
                  전문 컨설팅 / 개발전문회사
                </h1>
                <p className="leading-6 text-sm md:text-lg font-medium font-['Gmarket_Sans_TTF'] max-w-6xl lg:text-[20px] lg:leading-relaxed break-keep">
                  정부지원사업(스마트공장, 스마트공방, 혁신바우처) 컨설팅 및
                  구축, AI / SI / WEB 자동화 솔루션 개발, 제조 데이터 수집
                  플랫폼
                </p>
              </div>

              {/* Tab Section at bottom of hero */}
              <div className="w-full max-w-screen-2xl mx-auto">
                <div className="flex flex-col md:flex-row justify-center items-center md:items-end gap-2 md:gap-0">
                  <button
                    onClick={() => setActiveTab(0)}
                    className={`px-4 py-3 md:py-4 rounded-[20px] md:rounded-b-[0px] lg:rounded-b-[0px] \
                    shadow-[2px_-4px_15px_0px_rgba(0,0,0,0.30)] font-medium text-md sm:text-base md:text-lg \
                    font-['Gmarket_Sans_TTF'] leading-relaxed transition-colors w-full lg:w-[415px] \
                    lg:h-[70px] lg:text-[23px] lg:flex lg:items-center lg:justify-center \
                    ${
                      activeTab === 0
                        ? "bg-[#2c5aa0] text-white"
                        : "bg-zinc-400 text-white hover:bg-zinc-500"
                    } break-keep`}
                  >
                    정부지원사업 컨설팅
                  </button>
                  <button
                    onClick={() => setActiveTab(1)}
                    className={`px-4 py-3 md:py-4 rounded-[20px] md:rounded-b-[0px] lg:rounded-b-[0px] \
                    shadow-[2px_-4px_15px_0px_rgba(0,0,0,0.30)] font-medium text-md sm:text-base md:text-lg \
                    font-['Gmarket_Sans_TTF'] leading-relaxed transition-colors w-full lg:w-[415px] \
                    lg:h-[70px] lg:text-[23px] lg:flex lg:items-center lg:justify-center \
                    ${
                      activeTab === 1
                        ? "bg-[#2c5aa0] text-white"
                        : "bg-zinc-400 text-white hover:bg-zinc-500"
                    } break-keep`}
                  >
                    자동화 SW 시스템
                  </button>
                  <button
                    onClick={() => setActiveTab(2)}
                    className={`px-4 py-3 md:py-4 rounded-[20px] mb-3 md:mb-0 md:rounded-b-[0px] lg:rounded-b-[0px] \
                    shadow-[2px_-4px_15px_0px_rgba(0,0,0,0.30)] font-medium text-md sm:text-base md:text-lg \
                    font-['Gmarket_Sans_TTF'] leading-relaxed transition-colors w-full lg:w-[415px] \
                    lg:h-[70px] lg:text-[23px] lg:flex lg:items-center lg:justify-center \
                    ${
                      activeTab === 2
                        ? "bg-[#2c5aa0] text-white"
                        : "bg-zinc-400 text-white hover:bg-zinc-500"
                    } break-keep`}
                  >
                    제조 데이터 수집 플랫폼
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Tab Content Section - Responsive */}
          <section className="bg-gray-100 lg:px-[60px] xl:px-[140px] py-10 sm:py-14 md:py-16">
            <div className="max-w-screen-2xl mx-auto px-4">
              {activeTab === 0 && (
                <div className="flex flex-col lg:flex-row items-center justify-center gap-y-4">
                  <div className="text-center lg:text-left lg:w-[704px] lg:flex lg:items-center">
                    <h2 className="text-2xl sm:text-2xl md:text-4xl font-medium font-['Gmarket_Sans_TTF'] leading-tight text-gray-900 lg:text-[45px] lg:leading-tight">
                      스마트{" "}
                      <span className="text-[#2c5aa0]">
                        <br />
                        제조지원사업
                      </span>
                      이란?
                    </h2>
                  </div>
                  <div className="px-5 whitespace-nowrap bg-white rounded-[20px] shadow-lg p-6 md:p-8 max-w-lg lg:w-[530px] lg:h-[163px] lg:flex lg:flex-col lg:justify-center">
                    <p className="text-sm sm:text-lg md:text-xl font-bold font-['Gmarket_Sans_TTF'] leading-relaxed text-gray-700 mb-4 lg:text-[23px]">
                      소상공인진흥공단에서 주관하는 사업으로
                      <br />
                      10인 미만의 제조업이라면 신청 가능
                    </p>
                    <p className="text-[#2c5aa0] text-sm sm:text-lg md:text-xl font-bold font-['Gmarket_Sans_TTF'] leading-relaxed lg:text-[23px]">
                      "소상공인의 성장을 돋는 베스트 파트너"
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 1 && (
                <div className="flex flex-col lg:flex-row items-center justify-center gap-y-4">
                  <div className="text-center lg:text-left lg:w-[704px] lg:flex lg:items-center">
                    <h2 className="text-2xl sm:text-2xl md:text-4xl font-medium font-['Gmarket_Sans_TTF'] leading-tight text-gray-900 lg:text-[45px] lg:leading-tight">
                      자동화 수집
                      <br />
                      <span className="text-[#2c5aa0]">SW 시스템</span>이란?
                    </h2>
                  </div>
                  <div className="px-5 whitespace-nowrap bg-white rounded-[20px] shadow-lg p-6 md:p-8 max-w-lg lg:w-[530px] lg:h-[163px] lg:flex lg:flex-col lg:justify-center">
                    <p className="text-sm sm:text-lg md:text-xl font-bold font-['Gmarket_Sans_TTF'] leading-relaxed text-gray-700 mb-4 lg:text-[23px]">
                      업무의 효율성과 빠른 의사 결정,
                      <br />
                      오류 감소를 도와주는 시스템
                    </p>
                    <p className="text-[#2c5aa0] text-sm sm:text-lg md:text-xl font-bold font-['Gmarket_Sans_TTF'] leading-relaxed lg:text-[23px]">
                      "반복적인 작업은 이제 그만"
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 2 && (
                <div className="flex flex-col lg:flex-row items-center justify-center gap-y-4">
                  <div className="text-center lg:text-left lg:w-[704px] lg:flex lg:items-center">
                    <h2 className="text-2xl sm:text-2xl md:text-4xl font-medium font-['Gmarket_Sans_TTF'] leading-tight text-gray-900 lg:text-[45px] lg:leading-tight">
                      제조 데이터 수집장치
                      <br />
                      <span className="text-[#2c5aa0]">IoT Platform</span> 이란?
                    </h2>
                  </div>
                  <div className="px-5 whitespace-nowrap bg-white rounded-[20px] shadow-lg p-6 md:p-8 max-w-lg lg:w-[530px] lg:h-[163px] lg:flex lg:flex-col lg:justify-center">
                    <p className="text-sm sm:text-lg md:text-xl font-bold font-['Gmarket_Sans_TTF'] leading-relaxed text-gray-700 mb-4 lg:text-[23px]">
                      상업용 기계의 데이터 자동화
                      <br />
                      수집, 통계, DB
                    </p>
                    <p className="text-[#2c5aa0] text-sm sm:text-lg md:text-xl font-bold font-['Gmarket_Sans_TTF'] leading-relaxed lg:text-[23px]">
                      "머신비전데이터 수집 및 활용"
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Point 01 Section - Responsive */}
          <section
            id="services"
            className="relative py-[30px] sm:py-[60px] md:py-[7.5625rem] bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('/assets/images/study-789631 2.png')`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-800/90 via-slate-400/90 to-blue-300/90"></div>
            <div className="relative z-10 max-w-screen-2xl mx-auto px-4 text-center text-white">
              <div className="mb-[10px] ">
                <span className="text-sm sm:text-xl md:text-2xl font-bold font-['Gmarket_Sans_TTF'] leading-normal lg:text-[23px]">
                  Point 01
                </span>
              </div>

              <div className="bg-[#ffd7fb] text-[#3e4899] px-2 lg:px-3 rounded-lg inline-block mb-6 md:mb-8 pt-2 pb-1">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold font-['Gmarket_Sans_TTF'] leading-none lg:text-[60px] lg:leading-none">
                  스마트제조지원사업
                </h2>
              </div>

              <div className="mb-[2.0rem] md:mb-[3.5625rem]">
                <p className="break-keep leading-6 text-base sm:text-md md:text-xl font-bold font-['NanumSquareOTF'] max-w-4xl mx-auto mb-1.5  lg:max-w-6xl lg:whitespace-nowrap">
                  "인쇄 · 제조업의 온라인 판매 및 생산관리를 위한 솔루션으로
                  클라우드 기반으로 제공"
                </p>
                <p className="leading-6 sm:text-md md:text-xl font-bold font-['NanumSquareOTF'] lg:text-[20px]">
                  (월 임대로 - 부가세 별도)
                </p>
              </div>

              {/* Service Cards - Responsive Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-4 mb-[1.5rem] max-w-6xl mx-auto">
                <div
                  className="bg-white rounded-[25px] p-6 md:p-8 md:px-6 text-center \
                  shadow-lg flex flex-col justify-center relative overflow-hidden w-[100%] \
                  lg:h-[228px] lg:p-6"
                >
                  <div
                    className="absolute top-[40%] bg-no-repeat opacity-100 w-[80%] \
                    h-[50%] md:h-[40%] lg:h-[35%]"
                    style={{
                      left: "50%",
                      marginLeft: "-40%",
                      backgroundImage: `url('/assets/images/pinkpenpoint.png')`,
                      backgroundSize: "contain",
                      backgroundPosition: "center center",
                    }}
                  />
                  <h3 className="text-lg md:text-xl font-extrabold text-black font-['NanumSquareOTF'] relative z-10 lg:text-[24px]">
                    사전 컨설팅
                  </h3>
                  <p
                    className="text-lg md:text-xl font-bold text-gray-800 leading-7 md:leading-tight font-['Gmarket_Sans_TTF'] relative z-10 lg:text-[28px]"
                    style={{ marginTop: "22px" }}
                  >
                    소상공인별 과제추진
                    <br />
                    방향 설정 및 계획
                  </p>
                </div>

                <div className="bg-white rounded-[25px] p-6 md:p-8 md:px-6 text-center shadow-lg flex flex-col justify-center relative overflow-hidden w-[100%] lg:h-[228px] lg:p-6">
                  <div
                    className="absolute top-[40%] bg-no-repeat opacity-100 w-[80%] h-[50%] md:h-[40%] lg:h-[35%]"
                    style={{
                      left: "50%",
                      marginLeft: "-40%",
                      backgroundImage: `url('/assets/images/pinkpenpoint.png')`,
                      backgroundSize: "contain",
                      backgroundPosition: "center center",
                    }}
                  />
                  <h3 className="text-lg md:text-xl font-extrabold text-black font-['NanumSquareOTF'] relative z-10 lg:text-[24px]">
                    하드웨어 지원
                  </h3>
                  <p
                    className="text-lg md:text-xl font-bold text-gray-800 leading-tight md:leading-tight font-['Gmarket_Sans_TTF'] relative z-10 lg:text-[28px]"
                    style={{ marginTop: "22px" }}
                  >
                    하드웨어 임차비
                    <br />
                    데이터 자동연계 장치
                  </p>
                </div>

                <div className="bg-white rounded-[25px] p-6 md:p-8 md:px-6 text-center shadow-lg flex flex-col justify-center relative overflow-hidden w-[100%] lg:h-[228px] lg:p-6">
                  <div
                    className="absolute top-[40%] bg-no-repeat opacity-100 w-[80%] h-[50%] md:h-[40%] lg:h-[35%]"
                    style={{
                      left: "50%",
                      marginLeft: "-40%",
                      backgroundImage: `url('/assets/images/pinkpenpoint.png')`,
                      backgroundSize: "contain",
                      backgroundPosition: "center center",
                    }}
                  />
                  <h3 className="text-lg md:text-xl font-extrabold text-black font-['NanumSquareOTF'] relative z-10 lg:text-[24px]">
                    소프트웨어 지원
                  </h3>
                  <p
                    className="text-lg md:text-xl font-bold text-gray-800 leading-tight md:leading-tight font-['Gmarket_Sans_TTF'] relative z-10 lg:text-[28px]"
                    style={{ marginTop: "22px" }}
                  >
                    스마트화를 위한
                    <br />
                    소프트웨어 임차비
                  </p>
                </div>
              </div>

              {/* 정부지원금 안내 텍스트 */}
              <div className="text-right flex-row w-[100%] justify-end max-w-6xl mx-auto">
                <p
                  className="text-center text-xs font-bold font-['NanumSquareOTF'] lg:text-[15px]"
                  style={{ left: "80%" }}
                >
                  *년도별 정부지원금 지원금액 상이
                </p>
              </div>

              <div className="text-center relative">
                <p
                  className="text-md leading-7 md:text-2xl lg:text-3xl font-bold md:leading-[2.4rem] lg:leading-[3.0938rem] \
              font-['Gmarket_Sans_TTF'] max-w-4xl mx-auto pt-[2rem] md:pt-[3.5625rem] break-keep"
                >
                  수기 위주의 작업 공정의 디지털 전환을 위한
                  <br />
                  자동화 기기 도입 및 주문 공정 관리 솔루션 도입으로 스마트화
                  지원
                </p>
                <div className="h-[3px] md:h-[6px] bg-[#ffd7fb] max-w-4xl mx-auto"></div>
              </div>
            </div>
          </section>

          {/* POD24 Automation Solution Section - Responsive */}
          <section
            className="relative py-[40px] sm:py-[60px] md:py-[160px] bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('/assets/images/startup-593338 2.png')`,
            }}
          >
            <div className="absolute inset-0 bg-black bg-opacity-75"></div>
            <div className="relative z-10 w-full text-white">
              <div className="text-center mb-[1.5rem] md:mb-[3em] max-w-screen-2xl mx-auto px-4">
                <h2 className="flex flex-col items-center md:flex-row md:justify-center gap-2 md:gap-3 text-3xl md:text-5xl font-bold font-['Gmarket_Sans_TTF'] leading-[48px] mb-3 md:mb-6 lg:text-[60px]">
                  <span>POD24</span>
                  <span className="relative inline-block">
                    <div className="absolute -top-2 md:-top-6 left-0 right-0 flex">
                      <span className="relative flex-1 text-center">
                        <div className="w-2 h-2 md:w-3 md:h-3 bg-[#ffd7fb] rounded-full mx-auto"></div>
                      </span>
                      <span className="relative flex-1 text-center">
                        <div className="w-2 h-2 md:w-3 md:h-3 bg-[#ffd7fb] rounded-full mx-auto"></div>
                      </span>
                      <span className="relative flex-1 text-center">
                        <div className="w-2 h-2 md:w-3 md:h-3 bg-[#ffd7fb] rounded-full mx-auto"></div>
                      </span>
                      <span className="relative flex-1 text-center">
                        <div className="w-2 h-2 md:w-3 md:h-3 bg-[#ffd7fb] rounded-full mx-auto"></div>
                      </span>
                      <span className="relative flex-1 text-center">
                        <div className="w-2 h-2 md:w-3 md:h-3 bg-[#ffd7fb] rounded-full mx-auto"></div>
                      </span>
                      <span className="relative flex-1 text-center">
                        <div className="w-2 h-2 md:w-3 md:h-3 bg-[#ffd7fb] rounded-full mx-auto"></div>
                      </span>
                    </div>
                    자동화 솔루션
                  </span>
                </h2>
                <p className="text-base sm:text-md md:text-xl font-bold font-['NanumSquareOTF']">
                  소상공인 맞춤형 기능제공
                </p>
              </div>

              {/* Feature Highlights */}
              <div className="flex flex-col items-center mb-[1rem] md:mb-[4.5rem]">
                <div
                  className="bg-[#2c5aa0] rounded-[25px] md:rounded-[3em] p-10 flex flex-col md:flex-row gap-x-10 gap-y-2 / py-5 lg:py-[30px] / mx-5 ml:mx-0"
                >
                  <div>
                    <span className="text-2xl md:text-3xl lg:text-[35px] font-bold text-white font-['Gmarket_Sans_TTF']">
                      만족도
                    </span>{" "}
                    <span className="text-2xl md:text-3xl lg:text-[35px] font-bold text-[#ffd7fb] leading-normal font-['Gmarket_Sans_TTF']">
                      UP
                    </span>
                  </div>
                  <div>
                    <span className="text-2xl md:text-3xl lg:text-[35px] font-bold text-white font-['Gmarket_Sans_TTF']">
                      효율성
                    </span>{" "}
                    <span className="text-2xl md:text-3xl lg:text-[35px] font-bold text-[#ffd7fb] leading-normal font-['Gmarket_Sans_TTF']">
                      UP
                    </span>
                  </div>
                  <div>
                    <span className="text-2xl md:text-3xl lg:text-[35px] font-bold text-white font-['Gmarket_Sans_TTF']">
                      편리성
                    </span>{" "}
                    <span className="text-2xl md:text-3xl lg:text-[35px] font-bold text-[#ffd7fb] leading-normal font-['Gmarket_Sans_TTF']">
                      UP
                    </span>
                  </div>
                </div>
              </div>

              {/* Features Grid - Responsive Layout */}
              <div className="flex flex-col items-center \ text-left \ overflow-hidden">
                <div
                  className="grid grid-cols-1 md:grid-cols-2 min-[1250px]:grid-cols-3 max-[820px]:grid-cols-1
                \ gap-x-[40px] gap-y-[25px] md:gap-y-[50px] \ w-[100%] max-w-[1200px] \ max-[1200px]:px-4"
                >
                  <div className="flex flex-col items-center \ relative">
                    <div className="flex flex-col items-center md:items-start \ gap-2 \ md:w-[350px] lg:w-[400px]">
                      <div className="absolute right-0 h-1 bg-[#555555]"></div>
                      <p
                        className="font-['Gmarket_Sans_TTF'] \ text-4xl lg:text-5xl
                    \ text-white font-bold leading-21 \ md:mb-[12px] mt-6"
                      >
                        01
                      </p>
                      <p
                        className="font-['Gmarket_Sans_TTF']
                      \ text-white text-2xl md:text-3xl font-bold  lg:text-[35px]"
                      >
                        직관적으로 구성된
                      </p>
                      <p
                        className="text-[#2c5aa0] bg-[#ffd7fb] \ text-2xl md:text-3xl font-bold  \
                    leadipx-1 inline-block font-['Gmarket_Sans_TTF'] lg:text-[35px] py-1 pt-2"
                      >
                        쉬운 쇼핑몰
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-center \ relative">
                    <div className="flex flex-col items-center md:items-start \ gap-2 \ md:w-[350px] lg:w-[400px]">
                      <div className="absolute left-[-10000px] right-0 h-1 bg-[#555555]"></div>
                      <p
                        className="font-['Gmarket_Sans_TTF'] \ text-4xl lg:text-5xl
                    \ text-white font-bold leading-21 \ md:mb-[14px] mt-6"
                      >
                        02
                      </p>
                      <p
                        className="text-[#2c5aa0] bg-[#ffd7fb] \ text-2xl md:text-3xl font-bold  \
                    leadipx-1 inline-block font-['Gmarket_Sans_TTF'] lg:text-[35px] py-1 pt-2"
                      >
                        셀프 디자인 편집기
                      </p>
                      <p
                        className="font-['Gmarket_Sans_TTF']
                      \ text-white text-2xl md:text-3xl font-bold lg:text-[35px]"
                      >
                        고객 니즈 UP
                      </p>
                    </div>
                  </div>
                  <div className="flex-col items-center \ hidden lg:flex" />
                  <div className="flex-col items-center \ hidden lg:flex" />
                  <div className="flex flex-col items-center \ relative">
                    <div className="flex flex-col items-center md:items-start \ gap-2 \ md:w-[350px] lg:w-[400px]">
                      <div className="absolute right-[-10000px] left-0 h-1 bg-[#555555]"></div>
                      <p
                        className="font-['Gmarket_Sans_TTF'] \ text-4xl lg:text-5xl
                    \ text-white font-bold leading-21 \ md:mb-[14px] mt-6"
                      >
                        03
                      </p>
                      <p
                        className="font-['Gmarket_Sans_TTF']
                      \ text-white text-2xl md:text-3xl font-bold lg:text-[35px]"
                      >
                        오프라인 거래처
                      </p>
                      <p
                        className="text-[#2c5aa0] bg-[#ffd7fb] \ text-2xl md:text-3xl font-bold  \
                    leadipx-1 inline-block font-['Gmarket_Sans_TTF'] lg:text-[35px] py-1 pt-2"
                      >
                        후불 장부 기능으로
                      </p>
                      <p
                        className="font-['Gmarket_Sans_TTF']
                      \ text-white text-2xl md:text-3xl font-bold  lg:text-[35px]"
                      >
                        관리 가능
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-center \ relative">
                    <div className="flex flex-col items-center md:items-start \ gap-2 \ md:w-[350px] lg:w-[400px]">
                      <div className="absolute right-[-10000px] left-0 h-1 bg-[#555555]"></div>
                      <p
                        className="font-['Gmarket_Sans_TTF'] \ text-4xl lg:text-5xl
                    \ text-white font-bold leading-21 \ md:mb-[14px] mt-6"
                      >
                        04
                      </p>
                      <p
                        className="text-[#2c5aa0] bg-[#ffd7fb] \ text-2xl md:text-3xl font-bold  \
                    leadipx-1 inline-block font-['Gmarket_Sans_TTF'] lg:text-[35px] py-1 pt-2"
                      >
                        실시간 칸반보드
                      </p>
                      <p
                        className="font-['Gmarket_Sans_TTF']
                      \ text-white text-2xl md:text-3xl font-bold  lg:text-[35px]"
                      >
                        내부 직원간 업무 공유 및
                      </p>
                      <p
                        className="font-['Gmarket_Sans_TTF']
                      \ text-white text-2xl md:text-3xl font-bold  lg:text-[35px]"
                      >
                        고객 상품 주문 관리
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-center \ relative">
                    <div className="flex flex-col items-center md:items-start \ gap-2 \ md:w-[350px] lg:w-[400px]">
                      <div className="absolute left-[-10000px] right-0 h-1 bg-[#555555]"></div>
                      <p
                        className="font-['Gmarket_Sans_TTF'] \ text-4xl lg:text-5xl
                    \ text-white font-bold leading-21 \ md:mb-[14px] mt-6"
                      >
                        05
                      </p>
                      <p
                        className="font-['Gmarket_Sans_TTF']
                      \ text-white text-2xl md:text-3xl font-bold  lg:text-[35px]"
                      >
                        업무 효율성을 위한
                      </p>
                      <p
                        className="text-[#2c5aa0] bg-[#ffd7fb] \ text-2xl md:text-3xl font-bold  \
                    leadipx-1 inline-block font-['Gmarket_Sans_TTF'] lg:text-[35px] py-1 pt-2"
                      >
                        자동견적 시스템 도입
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-center \ relative">
                    <div className="flex flex-col items-center md:items-start \ gap-2 \ md:w-[350px] lg:w-[400px]">
                      <div className="absolute left-[-10000px] right-0 h-1 bg-[#555555]"></div>
                      <p
                        className="font-['Gmarket_Sans_TTF'] \ text-4xl lg:text-5xl
                    \ text-white font-bold leading-21 \ md:mb-[14px] mt-6"
                      >
                        06
                      </p>
                      <p
                        className="text-[#2c5aa0] bg-[#ffd7fb] \ text-2xl md:text-3xl font-bold  \
                    leadipx-1 inline-block font-['Gmarket_Sans_TTF'] lg:text-[35px] py-1 pt-2"
                      >
                        자유로운 편집 가능
                      </p>
                      <p
                        className="font-['Gmarket_Sans_TTF']
                      \ text-white text-2xl md:text-3xl font-bold  lg:text-[35px]"
                      >
                        업무 편리성 UP
                      </p>
                    </div>
                  </div>
                  <div className="flex-col items-center \ hidden lg:flex" />
                </div>
              </div>
            </div>
          </section>

          {/* Point 02 Section - AI/SI/WEB Automation - Responsive */}
          <section id="about" className="py-[40px] sm:py-[60px] md:py-[160px] bg-white pb-0">
            <div className="max-w-screen-2xl mx-auto px-4">
              <div
                className="text-center mb-[30px] md:mb-[50px]"
              >
                <span className="text-[#3e4899] text-sm sm:text-xl md:text-2xl font-bold font-['Gmarket_Sans_TTF'] leading-normal lg:text-[23px]">
                  Point 02
                </span>
                <h2
                  className="mb-6 md:mb-8 break-keep text-3xl md:text-5xl font-bold text-black leading-[38px] font-['Gmarket_Sans_TTF'] lg:text-[60px]"
                  style={{ marginTop: "10px" }}
                >
                  AI · SI · WEB 자동화 솔루션
                </h2>
                <p
                  className="break-keep sm:text-md md:text-xl text-black font-bold max-w-4xl mx-auto font-['NanumSquareOTF']"
                >
                  분석, 시각화 및 알람 등으로 데이터 기반의 의사결정을 더욱 쉽고
                  빠르게 진행
                </p>
              </div>

              {/* Services Grid */}
              <div className="flex justify-center">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-10 mb-0 max-w-7xl">
                  <div className="text-center max-w-sm mx-auto">
                    <div className="w-20 h-20 mx-auto mb-[15px] flex items-center justify-center">
                      <img
                        src="/assets/icons/job-interview.svg"
                        alt="자동화 데이터 수집"
                        style={{ width: "77px", height: "77px" }}
                      />
                    </div>
                    <h3
                      className="text-2xl md:text-3xl font-bold text-black leading-normal mb-4 font-['Gmarket_Sans_TTF'] text-center"
                    >
                      자동화 데이터 수집
                    </h3>
                    <ul
                      className="text-base text-sm sm:text-lg md:text-xl text-left space-y-2 text-gray-700 font-normal font-['NanumSquareOTF'] list-disc ml-[20px] md:ml-[30px]"
                    >
                      <li>웹 사이트 정보 자동 수립</li>
                      <li>공개 API 기반 정보 자동 수립</li>
                      <li>주기적 / 이벤트적 수집 방식 지원</li>
                    </ul>
                  </div>

                  <div className="text-center max-w-sm mx-auto">
                    <div className="w-20 h-20 mx-auto mb-[15px] flex items-center justify-center">
                      <img
                        src="/assets/icons/open-book.svg"
                        alt="웹 서비스간 데이터 연동"
                        style={{ width: "77px", height: "77px" }}
                      />
                    </div>
                    <h3
                      className="text-2xl md:text-3xl font-bold text-black leading-tight mb-4 font-['Gmarket_Sans_TTF'] text-center"
                    >
                      웹 서비스간 데이터 연동
                    </h3>
                    <ul
                      className="text-base text-sm sm:text-lg md:text-xl text-left space-y-2 text-gray-700 font-normal font-['NanumSquareOTF'] list-disc ml-[20px] md:ml-[30px]"
                    >
                      <li>
                        웹 시스템 별 다른 통신
                        <br />
                        인터페이스 방식 지원 및 연동 지원
                      </li>
                      <li>
                        RESTful API, DB 연동등
                        <br />
                        범용 Protocol 기본 지원
                      </li>
                    </ul>
                  </div>

                  <div className="text-center max-w-sm mx-auto">
                    <div className="w-20 h-20 mx-auto mb-[15px] flex items-center justify-center">
                      <img
                        src="/assets/icons/Frame 11.svg"
                        alt="데이터 활용"
                        style={{ width: "98px", height: "103px" }}
                      />
                    </div>
                    <h3
                      className="text-2xl md:text-3xl font-bold text-black leading-tight mb-4 font-['Gmarket_Sans_TTF'] text-center"
                    >
                      데이터 활용
                    </h3>
                    <ul
                      className="text-base text-sm sm:text-lg md:text-xl text-left space-y-2 text-gray-700 font-normal font-['NanumSquareOTF'] list-disc ml-[20px] md:ml-[30px]"
                    >
                      <li>사용자 맞춤형 엑셀 리포팅</li>
                      <li>대시보드 시각화 지원</li>
                      <li>SNS 알림 및 이메일 발송 지원</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Architecture Diagram Section - White Background */}
          <section
            className="bg-white pb-16 md:pb-20"
            style={{ paddingTop: "50px" }}
          >
            <div className="max-w-screen-2xl mx-auto px-4">
              <div className="flex justify-center">
                <img
                  src="/assets/images/architecture-185832.png"
                  alt="IoT 데이터 플랫폼 구조도"
                  className="w-full max-w-4xl h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </section>

          {/* Point 03 Section - IoT Data Platform - Responsive */}
          <section
            id="cases"
            className="break-keep bg-[#2c5aa0] py-[40px] sm:py-[60px] md:py-[160px] text-white"
          >
            <div className="max-w-screen-2xl mx-auto px-6 text-center">
              <div className="mb-10 md:mb-14">
                <span className="text-sm sm:text-xl md:text-2xl font-bold font-['Gmarket_Sans_TTF'] leading-normal block mb-[10px]">
                  Point 03
                </span>
                <h2 className="text-3xl md:text-5xl font-bold font-['Gmarket_Sans_TTF'] break-keep">
                  "IoT 기반 데이터 플랫폼"
                </h2>
              </div>

              {/* Platform Description Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8 max-w-6xl mx-auto mb-4 md:mb-8">
                <div className="bg-white rounded-[20px] p-6 md:p-8 text-black shadow-lg flex items-center justify-center md:min-h-[200px]">
                  <p className="text-sm sm:text-lg md:text-xl leading-loose font-bold font-['Gmarket_Sans_TTF'] text-stone-900">
                    사물인터넷(Internet of Things) <br />
                    <span className="text-[#3e4899] bg-[#ffd7fb] px-1 py-0">
                      기술을 활용하여 수집된 데이터를 <br />
                      <span
                        className="underline"
                        style={{ textUnderlineOffset: "6px" }}
                      >
                        처리하고 분석하는 시스템
                      </span>
                    </span>
                  </p>
                </div>

                <div className="bg-white rounded-[20px] p-6 md:p-8 text-black shadow-lg flex items-center justify-center md:min-h-[200px]">
                  <p className="text-sm sm:text-lg md:text-xl leading-loose font-bold font-['Gmarket_Sans_TTF'] text-stone-900">
                    센서, 장치, 네트워크 등을 통해 <br />
                    <span className="text-[#3e4899] bg-[#ffd7fb] px-1 py-0">
                      다양한 데이터 소스를 연결
                      <br />
                      실시이메일 간으로 모니터링, 저장, 분석, 시각화
                    </span>
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-[20px] p-6 md:p-8 text-black shadow-lg max-w-6xl mx-auto flex items-center justify-center md:min-h-[200px]">
                <div className="text-center">
                  <p className="text-sm sm:text-lg md:text-xl leading-loose font-bold text-stone-900 font-['Gmarket_Sans_TTF']">
                    수집된 데이터를 분석하여 유의미한 인사이트 제공
                    <br />
                    <span className="text-[#3e4899] bg-[#ffd7fb] px-1 py-0 font-bold font-['Gmarket_Sans_TTF']">
                      실시간 대시보드나 경고 시스템 통해 사용자 알림
                      <br />
                      머신러닝 및 AI를 활용하여 데이터 패턴을 찾고 동향 예측
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Architecture Section - White Background */}
          <section className="bg-white py-10 md:py-20">
            <div className="max-w-screen-2xl mx-auto px-4">
              <div className="flex justify-center">
                <img
                  src="/assets/images/architecture-583483.png"
                  alt="제조 데이터 수집 시스템 구조도"
                  className="w-full max-w-5xl h-auto rounded-lg shadow-lg"
                />
              </div>
            </div>
          </section>

          {/* Logout Confirmation Dialog */}
          <Dialog
            isOpen={showLogoutDialog}
            onClose={() => setShowLogoutDialog(false)}
            onConfirm={handleLogout}
            title={
              <>
                정말 로그아웃을 <br />
                <b>진행하시겠어요?</b>
              </>
            }
            description="언제든지 다시 로그인하실 수 있어요."
            confirmText="로그아웃"
            cancelText="취소"
            confirmVariant="danger"
            isLoading={isLoading}
            logo={false}
          />

          {/* Login Modal */}
          <LoginModal
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
          />
        </div>
      </Pod24CommonLayout>
    </>
  );
};
